// Canonical standings-zone mapping (UI-SPEC §1.5 / §7 / K10).
//
// The BE (dashboard/utils/standings.py, PROMOTION_PALETTE) canonicalises the
// TheSports promotion names into zone codes and ships per-row
// `promotion: {id, name, color}` — but with the BE's OWN palette/wording
// (#1E40AF navy CL, #F59E0B amber EL, #22C55E green Conf, #EF4444 red Rel,
// German "Titel-/Euro-Quali", …). The UI-SPEC defines the page's zone colours
// and legend labels EXACTLY (§1.5):
//
//   Champions League    #2183FE   (DE: Champions League)
//   Europa League       #F5822B   (DE: Europa League)
//   Conference League   #1F9D55   (DE: Conference League)
//   Relegation play-off #EAB308   (DE: Relegation)
//   Relegation (direkt) #E1495C   (DE: Abstieg)
//
// So the FE maps the provider/BE zone onto the spec palette: BE canonical id
// first, then a defensive name match (mirrors the BE's keyword order for
// payloads that only carry raw provider names), and for zones OUTSIDE the
// spec's five (euq "Titel-/Euro-Quali", promotion, generic play-off,
// cup_winner, …) the provider's own name+color pass through unchanged —
// the FE never invents a zone (build brief / QA2 defect 3).
//
// The DE legend labels come from the static bundles: "Champions League" /
// "Europa League" / "Relegation"→"Abstieg" (components/home/home.json) and
// "Conference League" / "Relegation play-off"→"Relegation"
// (competitions-standings.json) are all registered — ZoneLegend runs names
// through tr(), so returning the canonical EN names here yields K10's DE.

export const ZONE_COLORS = {
  cl: "#2183FE",
  el: "#F5822B",
  ecl: "#1F9D55",
  po: "#EAB308",
  rel: "#E1495C",
};

const ZONES = {
  cl: { id: "cl", name: "Champions League", color: ZONE_COLORS.cl },
  el: { id: "el", name: "Europa League", color: ZONE_COLORS.el },
  ecl: { id: "ecl", name: "Conference League", color: ZONE_COLORS.ecl },
  po: { id: "po", name: "Relegation play-off", color: ZONE_COLORS.po },
  rel: { id: "rel", name: "Relegation", color: ZONE_COLORS.rel },
};

// BE canonical promotion ids (dashboard/utils/standings.py) → spec zone.
// Deliberately NOT mapped (intent, nailed by BE tests): euq, prom, prom_po,
// po (generic/continental play-off), cup_winner — those keep the provider
// identity via passthrough below.
const ID_TO_ZONE = {
  cl: "cl",
  el: "el",
  conf: "ecl",
  ecl: "ecl",
  rel_po: "po",
  rel: "rel",
};

// Defensive name matching for payloads without canonical ids. Keyword order
// mirrors the BE's `_canonical_zone_from_name` where it overlaps: the
// specific play-off variant BEFORE the base word, conference BEFORE
// champions ("Conference League" must not hit the generic checks first).
const nameToZone = (name) => {
  if (!name) return null;
  const n = String(name).trim().toLowerCase().replace(/\s+/g, " ");
  if (!n) return null;
  if (/relegation play.?offs?|relegation playoffs?/.test(n)) return "po";
  if (/promotion/.test(n)) return null; // promotion zones stay provider-owned
  if (/conference|\becl\b|\buecl\b/.test(n)) return "ecl";
  if (/europa|\buel\b/.test(n)) return "el";
  if (/champions|\bucl\b|\bcl\b/.test(n)) return "cl";
  if (/relegation|relegated|abstieg|descenso|degrade/.test(n)) return "rel";
  return null;
};

// promotion {id?, name?, color?} → {id, name, color} in spec palette, or the
// provider entry passed through for unknown zones, or null when there is
// nothing renderable.
export const canonicalPromotion = (promotion) => {
  if (!promotion) return null;
  const zone =
    ID_TO_ZONE[promotion.id] != null
      ? ID_TO_ZONE[promotion.id]
      : nameToZone(promotion.name);
  if (zone) return ZONES[zone];
  if (promotion.name && promotion.color) {
    return {
      id: promotion.id || promotion.name,
      name: promotion.name,
      color: promotion.color,
    };
  }
  return null;
};

export default canonicalPromotion;
